'use client'

import { useState } from 'react'

import { track } from '@amplitude/analytics-browser'

import { browserNavigate } from '@/lib/browser-navigation'
import { buildInquiryDraft, buildInquiryMailto } from '@/lib/partners/inquiry'
import {
    DETAILS_MAX_LENGTH,
    DETAILS_PLACEHOLDER,
    PARTNERSHIPS_EMAIL,
} from '@/content/partners/data'
import type { InquiryFields } from '@/content/partners/types'

// Referral tokens are campaign slugs. The query string is the surface's only input
// no field bounds, so anything longer or stranger than a slug is ignored — it would
// otherwise ride into the draft body (past the details field's length budget) and the analytics
// property.
const REF_TOKEN_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/

/**
 * The conversion event's payload per the ratified contract
 * (strategy `amplitude-events-partners.md`): the inquiry's SHAPE only — booleans,
 * the handoff method, the referral token. Never a field the operator typed.
 */
const inquiryEventProperties = (
    method: 'mailto' | 'draft_copy',
    { details, ref }: Pick<InquiryFields, 'details' | 'ref'>,
) => ({
    submission_method: method,
    has_details: Boolean(details),
    has_referral_code: Boolean(ref),
    ...(ref ? { referral_code: ref } : {}),
})

/**
 * The get-started conversion: composes an email instead of submitting anywhere — the
 * partner data stack is sunset, so the partnerships mailbox is the pipeline of record
 * Field values never leave the browser.
 */
export const GetStartedForm = (): React.ReactElement => {
    const [composed, setComposed] = useState<InquiryFields | null>(null)

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const form = new FormData(event.currentTarget)
        const companyValue = form.get('company')
        const company = typeof companyValue === 'string' ? companyValue.trim() : ''
        // Native `required` validation is the user-visible gate, but it accepts
        // whitespace-only input — the trim guard is what rejects that.
        if (!company) {
            return
        }

        const detailsValue = form.get('details')
        const details = (typeof detailsValue === 'string' ? detailsValue.trim() : '') || undefined
        // Read at submit time so the page can stay static — no searchParams on render.
        const refValue = new URLSearchParams(window.location.search).get('ref')
        const ref = refValue && REF_TOKEN_PATTERN.test(refValue) ? refValue : undefined

        const fields = { company, details, ref }
        // Track first: mailto rarely unloads the document, but navigate-then-track
        // would lose the event in the cases where it does.
        track('Partner Inquiry Submitted', inquiryEventProperties('mailto', fields))
        browserNavigate(buildInquiryMailto(fields))
        setComposed(fields)
    }

    const draft = composed ? buildInquiryDraft(composed) : null
    const composedText = draft
        ? `To: ${PARTNERSHIPS_EMAIL}\nSubject: ${draft.subject}\n\n${draft.body}`
        : ''

    const handleCopy = () => {
        // Optional-chains away on a non-secure context — and then nothing was
        // copied, so no draft_copy event should claim otherwise.
        const write = navigator.clipboard?.writeText(composedText)
        if (write === undefined) {
            return
        }
        void write.then(
            () => {
                if (composed) {
                    // Always a follow-on to a submit — the panel this button lives
                    // in only exists in the composed state (draft_copy ⊂ mailto).
                    track(
                        'Partner Inquiry Submitted',
                        inquiryEventProperties('draft_copy', composed),
                    )
                }
            },
            () => undefined,
        )
    }

    return (
        <div>
            <form onSubmit={handleSubmit}>
                <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-1.5">
                        <label
                            htmlFor="partner-company"
                            className="text-sm font-semibold text-foreground"
                        >
                            Company name
                        </label>
                        <input
                            id="partner-company"
                            name="company"
                            type="text"
                            required
                            autoComplete="organization"
                            className="rounded-md border border-input bg-background px-3 py-2.5 text-base focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label
                            htmlFor="partner-details"
                            className="text-sm font-semibold text-foreground"
                        >
                            Anything else we should know?{' '}
                            <span className="font-normal text-muted-foreground">(optional)</span>
                        </label>
                        <textarea
                            id="partner-details"
                            name="details"
                            rows={4}
                            maxLength={DETAILS_MAX_LENGTH}
                            placeholder={DETAILS_PLACEHOLDER}
                            className="rounded-md border border-input bg-background px-3 py-2.5 text-base focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                        />
                    </div>
                    <button
                        type="submit"
                        className="rounded-md bg-strong px-8 py-3 text-base font-semibold text-white shadow-sm hover:bg-strong-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-strong"
                    >
                        Compose email
                    </button>
                </div>
            </form>

            {composed && (
                <div
                    role="status"
                    className="mt-8 rounded-md border border-[var(--smoke-100)] bg-[var(--smoke-50)] p-5 text-sm"
                >
                    <p className="font-semibold">
                        Your email app should have opened. If it didn&apos;t, copy the message below
                        and send it to {PARTNERSHIPS_EMAIL}.
                    </p>
                    <div className="mt-4 font-mono text-xs leading-5 whitespace-pre-wrap">
                        {composedText}
                    </div>
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="mt-4 rounded-md border border-input px-4 py-2 font-semibold hover:bg-accent"
                    >
                        Copy email
                    </button>
                </div>
            )}
        </div>
    )
}
